import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, X, FileDown } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const GROUPS = [
    { v: "all", l: "Todos" },
    { v: "alumno", l: "Alumnos" },
    { v: "profesor", l: "Profesores" },
    { v: "padre", l: "Padres" },
    { v: "psicologo", l: "Psicólogos" },
];

export default function Comunicados() {
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [recipients, setRecipients] = useState(["all"]);
    const [sending, setSending] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    async function load() {
        const r = await api.get("/communications");
        setItems(r.data || []);
    }
    useEffect(() => {
        load();
    }, []);

    async function send() {
        setSending(true);
        try {
            await api.post("/communications", {
                title,
                message,
                recipients,
                file_id: attachment?.id || null,
            });
            toast.success("Comunicado enviado");
            setTitle("");
            setMessage("");
            setRecipients(["all"]);
            setAttachment(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSending(false);
        }
    }

    async function onPickFile(ev) {
        const file = ev.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachment(data);
            toast.success("Adjunto cargado");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo archivo");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    function toggle(v) {
        if (v === "all") {
            setRecipients(["all"]);
            return;
        }
        const next = recipients.filter((x) => x !== "all");
        setRecipients(next.includes(v) ? next.filter((x) => x !== v) : [...next, v]);
    }

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="admin-comunicados-page">
            <h1 className="text-3xl font-semibold mb-1">Comunicados</h1>
            <p className="text-edu-body mb-6">Envía anuncios oficiales del colegio. Puedes adjuntar PDFs o imágenes.</p>

            <div className="edu-card p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Crear comunicado</h3>
                <div className="space-y-3">
                    <input
                        placeholder="Título"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="edu-input w-full"
                        data-testid="comm-title"
                    />
                    <textarea
                        placeholder="Mensaje…"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={5}
                        className="edu-input w-full"
                        data-testid="comm-message"
                    />
                    <div>
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2 block">
                            Destinatarios
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {GROUPS.map((g) => (
                                <button
                                    key={g.v}
                                    onClick={() => toggle(g.v)}
                                    className={`edu-pill border ${recipients.includes(g.v) ? "bg-edu-navy text-white border-edu-navy" : "border-edu hover:bg-gray-50"}`}
                                    data-testid={`group-${g.v}`}
                                >
                                    {g.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-2 block">
                            Adjunto (opcional)
                        </span>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,image/*"
                            onChange={onPickFile}
                            className="hidden"
                            data-testid="comm-file-input"
                        />
                        {attachment ? (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-edu-soft border border-edu max-w-sm">
                                <Paperclip size={14} className="text-edu-navy" />
                                <span className="text-sm flex-1 truncate" title={attachment.original_filename}>
                                    {attachment.original_filename}
                                </span>
                                <button
                                    onClick={() => setAttachment(null)}
                                    className="text-edu-muted hover:text-red-700"
                                    data-testid="comm-file-clear"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-edu hover:bg-gray-50 disabled:opacity-50"
                                data-testid="comm-file-upload"
                            >
                                <Paperclip size={14} /> {uploading ? "Subiendo…" : "Adjuntar PDF o imagen"}
                            </button>
                        )}
                    </div>

                    <button
                        onClick={send}
                        disabled={!title || !message || sending}
                        className="edu-btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="comm-send"
                    >
                        <Send size={14} /> Enviar
                    </button>
                </div>
            </div>

            <h3 className="text-lg font-semibold mb-3">Comunicados enviados</h3>
            <div className="space-y-3">
                {items.map((c) => (
                    <div key={c.id} className="edu-card p-5" data-testid={`comm-item-${c.id}`}>
                        <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold">{c.title}</h4>
                            <span className="text-xs text-edu-muted">{(c.created_at || "").slice(0, 10)}</span>
                        </div>
                        <p className="text-sm text-edu-body whitespace-pre-line">{c.message}</p>
                        {c.file_id && (
                            <a
                                href={fileUrl(c.file_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                data-testid={`comm-attachment-${c.id}`}
                            >
                                <FileDown size={12} /> Descargar adjunto
                            </a>
                        )}
                        <div className="flex gap-1 mt-2">
                            {c.recipients.map((r) => (
                                <span key={r} className="edu-pill bg-edu-navy/10 text-edu-navy">
                                    {r}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
